package one.healthium.login.ui.login

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import ifaandroid.feature.login.login_ui.generated.resources.Res
import ifaandroid.feature.login.login_ui.generated.resources.ic_eye_24
import ifaandroid.feature.login.login_ui.generated.resources.ic_eye_off_24
import loco.LOGIN_CONTACT_US_BUTTON
import loco.LOGIN_EMAIL_PLACEHOLDER
import loco.LOGIN_FEEDBACK_EMAIL_SUBJECT_FORMAT
import loco.LOGIN_LOGIN_BUTTON
import loco.LOGIN_PASSWORD_PLACEHOLDER
import loco.LOGIN_SCREEN_TITLE
import one.healthium.base.launcher.LocalPlatformLauncher
import one.healthium.base.sender.EmailSender
import one.healthium.base.utils.BundleUtils
import one.healthium.domain.user.UserModel
import one.healthium.domain.user.models.SignInOptions
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppBackNavigationButton
import one.healthium.ui.compose.components.AppButton
import one.healthium.ui.compose.components.AppLoadingScreen
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppTextButton
import one.healthium.ui.compose.components.AppTextField
import one.healthium.ui.compose.components.AppTextFieldDefaults
import one.healthium.ui.compose.components.AppTopBar
import one.healthium.ui.compose.components.AppTopBarDefaults
import one.healthium.ui.compose.platform.PlatformColorsEffect
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_1
import one.healthium.ui.compose.theme.gray_3
import one.primehealth.analytics.AppAnalytics
import one.primehealth.analytics.compose.AppEventEffect
import one.primehealth.analytics.events.Events
import one.primehealth.app.BaseAppConfig
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel
import kotlin.time.Duration.Companion.milliseconds

interface LoginScreenEventDelegate {
    fun onBackClicked()
    fun onUserSignedIn(user: UserModel)
}

@Composable
fun LoginScreen(
    eventDelegate: LoginScreenEventDelegate
) {
    PlatformColorsEffect(lightStatusBar = false)

    val platformLauncher = LocalPlatformLauncher.current
    val keyboardController = LocalSoftwareKeyboardController.current
    val appConfig = koinInject<BaseAppConfig>()
    val appAnalytics = koinInject<AppAnalytics>()

    val viewModel = koinViewModel<LoginViewModel>()
    val state by viewModel.state.collectAsState()

    AppEventEffect {
        // TODO pass origin
        logEvent(Events.LoginScreenViewed(Events.LoginScreenViewed.Origin.Onboarding))
    }

    val user = state.user
    LaunchedEffect(user) {
        if (user != null) {
            appAnalytics.logEvent(
                Events.UserSignedUp(email = user.profile.email, uid = user.id)
            )
            eventDelegate.onUserSignedIn(user)
        }
    }
    val feedbackEmailSubject = stringResource(
        resource = loco.Res.string.LOGIN_FEEDBACK_EMAIL_SUBJECT_FORMAT,
        BundleUtils.getAppName(platformLauncher),
    )

    LoginScreenContent(
        state = state,
        onBackClicked = {
            keyboardController?.hide()
            eventDelegate.onBackClicked()
        },
        onUsernameChanged = viewModel::onUsernameChanged,
        onPasswordChanged = viewModel::onPasswordChanged,
        onContactUsClicked = {
            keyboardController?.hide()
            EmailSender.sendEmail(
                platformLauncher = platformLauncher,
                email = appConfig.feedbackEmail,
                subject = feedbackEmailSubject,
            )
        },
        onSignInClicked = {
            appAnalytics.logEvent(Events.UserSignInAttempt(it.username))
            keyboardController?.hide()
            viewModel.onSignInClicked(it)
        },
    )

    AlertUiMessageHandler(viewModel.messages)
}

@Composable
internal fun LoginScreenContent(
    state: LoginUiState,
    onBackClicked: () -> Unit,
    onUsernameChanged: (String) -> Unit,
    onPasswordChanged: (String) -> Unit,
    onContactUsClicked: () -> Unit,
    onSignInClicked: (SignInOptions.Username) -> Unit,
) {
    AppScaffold(
        modifier = Modifier.testTag("login_screen"),
        topBar = {
            AppTopBar(
                title = {
                    Text(
                        text = stringResource(loco.Res.string.LOGIN_SCREEN_TITLE),
                        style = AppTheme.textStyles.headerSemiBold,
                    )
                },
                navigationIcon = { AppBackNavigationButton(onClick = onBackClicked) },
                colors = AppTopBarDefaults.transparent,
            )
        }
    ) {
        var passwordVisible by remember { mutableStateOf(false) }

        Column(
            modifier = Modifier
                .padding(it)
                .padding(horizontal = 24.dp)
                .fillMaxSize()
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            Spacer(Modifier.weight(1f))
            AppTextField(
                modifier = Modifier.fillMaxWidth().testTag("login_email"),
                value = state.signInOptions.username,
                onValueChange = onUsernameChanged,
                showKeyboardAutomaticallyIfEmpty = true,
                showKeyboardDelay = 500.milliseconds,
                colors = AppTextFieldDefaults.colorsGray,
                shape = AppTextFieldDefaults.shapeRounded,
                placeholder = { Text(stringResource(loco.Res.string.LOGIN_EMAIL_PLACEHOLDER)) },
                maxLines = 1,
                keyboardOptions = KeyboardOptions(
                    keyboardType = KeyboardType.Email,
                    imeAction = ImeAction.Next,
                ),
            )
            Spacer(Modifier.height(16.dp))
            val passwordText = state.signInOptions.password
            AppTextField(
                modifier = Modifier.fillMaxWidth().testTag("login_password"),
                value = passwordText,
                onValueChange = onPasswordChanged,
                colors = AppTextFieldDefaults.colorsGray,
                shape = AppTextFieldDefaults.shapeRounded,
                placeholder = { Text(stringResource(loco.Res.string.LOGIN_PASSWORD_PLACEHOLDER)) },
                maxLines = 1,
                keyboardOptions = KeyboardOptions(
                    keyboardType = KeyboardType.Password,
                    imeAction = ImeAction.Done,
                ),
                keyboardActions = KeyboardActions(
                    onDone = {
                        state.signInOptions.options?.let(onSignInClicked)
                    }
                ),
                visualTransformation = if (passwordText.isNotEmpty() && passwordVisible.not()) PasswordVisualTransformation() else VisualTransformation.None,
                trailingIcon = {
                    IconButton(
                        onClick = {
                            passwordVisible = !passwordVisible
                        },
                        modifier = Modifier.testTag("login_password_visibility_icon")
                    ) {
                        Icon(
                            painter = painterResource(
                                if (passwordVisible) Res.drawable.ic_eye_off_24 else Res.drawable.ic_eye_24
                            ),
                            tint = gray_1,
                            contentDescription = null,
                        )
                    }
                }
            )
            Spacer(Modifier.height(24.dp))

            Text(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp),
                text = remember {
                    buildAnnotatedString {
                        // TODO loco
                        val text = "💡 We sent you the email with instructions and your plan access details. " +
                                "You can find it in your inbox (or spam folder).\n\n" +
                                "Contact us if you can’t find the email or have other questions."
                        val boldText = "your inbox (or spam folder)."
                        val boldIndex = text.indexOf(boldText)
                        if (boldIndex > -1) {
                            append(text.subSequence(0, boldIndex))
                            withStyle(SpanStyle(fontWeight = FontWeight.Bold)) {
                                append(boldText)
                            }
                            append(text.subSequence(boldIndex + boldText.length, text.length))
                        } else {
                            append(text)
                        }
                    }
                },
                style = AppTheme.textStyles.caption,
                color = gray_3,
            )
            Spacer(Modifier.height(12.dp))
            AppTextButton(onClick = onContactUsClicked) {
                Text(stringResource(loco.Res.string.LOGIN_CONTACT_US_BUTTON))
            }
            Spacer(Modifier.height(16.dp))
            Spacer(Modifier.weight(1f))
            val options = state.signInOptions.options
            AppButton(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp)
                    .testTag("login_button"),
                enabled = options != null,
                onClick = { options?.let(onSignInClicked) },
            ) {
                Text(stringResource(loco.Res.string.LOGIN_LOGIN_BUTTON))
            }
            Spacer(Modifier.height(25.dp))
            Spacer(Modifier.navigationBarsPadding().imePadding())
        }
    }
    AppLoadingScreen(visible = state.loading, dim = true)
}